import { Component, Input, OnChanges } from '@angular/core';
import { Product } from '../../../model/product/product.model';
import { lineColorSet } from '../line-color-set';

/** The three LINE store "main image" variants, at their native pixel sizes. */
export type LineThumbnailVariant = 'store' | 'ios' | 'android';

const SIZES: { [ v in LineThumbnailVariant ]: { w: number; h: number } } = {
  store: { w: 198, h: 278 },
  ios: { w: 200, h: 284 },
  android: { w: 136, h: 202 },
};

/**
 * A LINE store thumbnail (the theme's "main image"), drawn live so it can be
 * tuned on the /line workbench and exported 1:1, no screenshots involved.
 *
 * Design (inspired by the theme tile card): the theme's wallpaper fills the
 * canvas with the round logo (shadow, no ring) floating high in its clear
 * area; a same-colour scrim pools at the bottom (white / off-white on Light
 * themes, near-black on Dark, the same base sets as the chat previews) and
 * carries the identity stack: a "CATEGORY, LIGHT|DARK" eyebrow whose dash is
 * a short line in the theme's primary colour (like the theme card), the theme
 * name, and the tag line.
 *
 * One layout serves all three variants: everything scales off the store size
 * via the `--s` factor, so the iOS / Android exports are the same design at
 * their own native px.
 */
@Component( {
  selector: 'app-line-thumbnail',
  templateUrl: './line-thumbnail.component.html',
  styleUrls: [ './line-thumbnail.component.scss' ],
} )
export class LineThumbnailComponent implements OnChanges {

  @Input() product!: Product;

  /** The theme's product line name (e.g. "Nature" / "Cities"). */
  @Input() category = '';

  @Input() variant: LineThumbnailVariant = 'store';

  w = SIZES.store.w;
  h = SIZES.store.h;
  /** Type/spacing scale relative to the store variant. */
  s = 1;

  name = '';
  logo = '';
  wallpaper = '';
  appearance = '';
  /** Up to three theme tags as one dim "a · b · c" line (text only, the
   *  compact palette skips colours, so circles could mislead). */
  tags = '';

  /** Canvas fallback colour when a theme has no wallpaper (the base set's
   *  bg: white-ish or near-black). */
  panel = '#17181a';
  /** Fully transparent stop of the same colour (avoids the grey haze a
   *  plain `transparent` stop mixes into light gradients). */
  panel0 = '#17181a00';
  fg = '#ffffff';
  fgDim = '#9b9ba0';

  /** The theme-colour dash in the eyebrow + the logo-part gradient. */
  accent = '#549EFF';
  accent2 = '#1ECFB6';

  /** Unique id stem for this tile's SVG gradient defs (three variants of the
   *  same theme render on one page, so the key alone would collide). */
  uid = 'thumb';

  ngOnChanges(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    const size = SIZES[ this.variant ] || SIZES.store;
    this.w = size.w;
    this.h = size.h;
    this.s = size.w / SIZES.store.w;

    this.name = p.name || '';
    this.logo = p.logo || '';
    this.wallpaper = p.backgroundImages?.length ? p.backgroundImages[ 0 ].src : '';
    this.appearance = p.appearance || '';
    this.tags = ( p.tags || [] ).slice( 0, 3 ).join( ' · ' );

    // The store thumbnail sits over the THEME's own wallpaper, so it follows the
    // theme's appearance, NOT the LINE chat base set (whose light-mauve Tokyo
    // values made the scrim + text vanish on the dark cityscape). Use the
    // appearance-neutral set for the scrim + text fallbacks.
    const set = lineColorSet( undefined, p.appearance );
    this.panel = set.bg;
    this.panel0 = set.bg + '00';
    // Texts take the THEME's own foregrounds (name = primary, eyebrow/tags =
    // secondary), falling back to the neutral set when a token is missing.
    this.fg = p.designTokens?.primaryForeground || set.fg;
    this.fgDim = p.designTokens?.secondaryForeground || set.fgDim;

    this.accent = p.designTokens?.primaryColor || set.outgoing;
    this.accent2 = p.designTokens?.secondaryColor || this.accent;
    this.uid = ( p.key || 'x' ) + '-' + this.variant;
  }
}
